import { memo, useState } from "react";
import { CheckRounded, ContentCopyRounded } from "@mui/icons-material";
import type { NormValue, TradeFields } from "../../helpers/extractTrade";
import CardSourceToggle from "./CardSourceToggle";
import {
  CardShell,
  DetailRows,
  PriceLadder,
  SignalPill,
  extraInfoRows,
  fmtExpiryShort,
  fmtMoney,
  highOf,
  isApprox,
  isUnlimited,
  lowOf,
  type AxisColumn,
  type DetailRow,
} from "./trade";

type Normalized = Record<string, { raw: string; value: NormValue }>;

type TradeCardProps = {
  ticker: string;
  fields: TradeFields;
  normalized: Normalized;
  // source text this card derives from; memo() compares it instead of the objects
  raw?: string;
  companyName?: string;
};

const VerbBadge = ({ verb }: { verb: string }) => {
  const tone = /sell/i.test(verb) ? "border-error-main text-error-main" : "border-success-main text-success-main";
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.16px] ${tone}`}>
      {verb}
    </span>
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label="Copy contract"
      className="shrink-0 text-token-text-secondary transition-colors hover:text-token-text-primary"
    >
      {copied
        ? <CheckRounded className="!h-4 !w-4 text-success-main" />
        : <ContentCopyRounded className="!h-4 !w-4" />}
    </button>
  );
};

const TradeCard = ({ ticker, fields, normalized: N, raw, companyName }: TradeCardProps) => {
  const signal = (N.signal?.value as string | undefined) ?? undefined;
  const bullish = signal?.toLowerCase() === "bullish";
  const bearish = signal?.toLowerCase() === "bearish";
  const signalTone = bullish ? "bull" : bearish ? "bear" : "neutral";

  const verb = N.buy_sell?.value ? String(N.buy_sell.value) : undefined;
  const instrument = N.call_put?.value ? String(N.call_put.value) : undefined;

  const spot = lowOf(N.current_price?.value);
  const strike = lowOf(N.strike?.value);
  const breakeven = lowOf(N.breakeven?.value);
  const entry = lowOf(N.entry_ask?.value);
  const maxLoss = lowOf(N.max_loss?.value);
  const stopLoss = lowOf(N.stop_loss?.value);
  const tpHigh = highOf(N.take_profit?.value);

  const expiryPill = fmtExpiryShort(N.expiry?.value) ?? N.expiry?.raw ?? null;
  const delta = N.delta?.value ? String(N.delta.value) : null;

  // reward:risk from the stop/entry/take-profit ladder, shown as "1 : X"
  const riskReward = (() => {
    if (entry == null || stopLoss == null || tpHigh == null) return null;
    const risk = entry - stopLoss;
    const reward = tpHigh - entry;
    if (risk <= 0 || reward <= 0) return null;
    return `1 : ${(reward / risk).toFixed(1)}`;
  })();

  const strikeStr = strike != null ? fmtMoney(strike, strike % 1 === 0 ? 0 : 2) : null;
  const contractTitle = [ticker, strikeStr, instrument].filter(Boolean).join(" ");
  const copyText = [verb, ticker, strikeStr, instrument, expiryPill].filter(Boolean).join(" ");

  const tpLow = lowOf(N.take_profit?.value);
  const takeProfitLabel = tpHigh == null
    ? null
    : tpLow != null && tpLow !== tpHigh
      ? `${fmtMoney(tpLow, 0)}-${fmtMoney(tpHigh, 0).replace("$", "")}`
      : fmtMoney(tpHigh, tpHigh % 1 === 0 ? 0 : 2);

  const maxGainStr = N.max_gain
    ? isUnlimited(N.max_gain.raw)
      ? "∞"
      : typeof N.max_gain.value === "number"
        ? fmtMoney(N.max_gain.value, 0)
        : N.max_gain.raw
    : null;
  const maxLossStr = maxLoss != null ? `-${fmtMoney(Math.abs(maxLoss), 0, isApprox(N.max_loss?.raw))}` : null;

  const axisColumns: AxisColumn[] = [
    spot != null ? { label: "Spot", value: fmtMoney(spot) } : null,
    strike != null ? { label: "Strike", value: fmtMoney(strike) } : null,
    breakeven != null ? { label: "Breakeven", value: fmtMoney(breakeven) } : null,
  ].filter((c): c is AxisColumn => c != null);
  const hasBand = axisColumns.length > 0 || stopLoss != null || tpHigh != null;

  // economics grid: gain/loss/RR on the left, POP/IV/delta on the right
  const leftRows: DetailRow[] = [
    maxGainStr != null ? { label: "Max Gain", value: maxGainStr, tone: "primary" } : null,
    maxLossStr != null ? { label: "Max Loss", value: maxLossStr, tone: "primary" } : null,
    riskReward ? { label: "Risk-to-Reward Ratio (RR)", value: riskReward, tone: "primary" } : null,
  ].filter((r): r is DetailRow => r != null);
  const rightRows: DetailRow[] = [
    N.pop && /\d/.test(String(N.pop.value)) ? { label: "Probability of Profit (POP)", value: N.pop.value as string, tone: "primary" } : null,
    N.iv ? { label: "Implied Volatility (IV)", value: N.iv.value as string, tone: "primary" } : null,
    delta ? { label: "Delta", value: delta, tone: "primary" } : null,
  ].filter((r): r is DetailRow => r != null);
  const hasGrid = leftRows.length > 0 || rightRows.length > 0;

  // bullet lines the schema doesn't map, surfaced verbatim under "Additional information"
  const extraRows = extraInfoRows(raw, N);

  return (
    <CardShell>
      <div className="flex flex-col gap-4">
        <section className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {verb && <VerbBadge verb={verb} />}
            <span className="truncate text-lg font-bold text-token-text-primary">{contractTitle}</span>
            {expiryPill && <span className="whitespace-nowrap text-sm text-token-text-secondary">{expiryPill}</span>}
            {copyText && <CopyButton text={copyText} />}
          </div>
          {signal && <SignalPill label={signal} tone={signalTone} />}
        </section>

        {companyName && <div className="-mt-2 text-sm text-token-text-secondary">{companyName}</div>}

        {entry != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-token-text-secondary">Entry Ask</span>
            <span className="text-sm font-bold text-token-text-primary">{`${fmtMoney(entry)} / contract`}</span>
          </div>
        )}

        {hasBand && (
          <PriceLadder
            columns={axisColumns}
            stopLoss={stopLoss != null ? fmtMoney(stopLoss) : null}
            takeProfit={takeProfitLabel}
          />
        )}

        {hasGrid && (
          <div className="grid grid-cols-1 gap-x-10 gap-y-2 sm:grid-cols-2">
            <DetailRows rows={leftRows} />
            <DetailRows rows={rightRows} />
          </div>
        )}

        {fields.strategy && !verb && !instrument && (
          <div className="text-sm text-token-text-secondary">{fields.strategy}</div>
        )}

        {extraRows.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs uppercase tracking-wide text-token-text-secondary">Additional information</span>
            <ul className="flex list-disc flex-col gap-1 pl-5">
              {extraRows.map(r => (
                <li key={r.label} className="text-sm text-token-text-primary marker:text-token-text-secondary">
                  <span className="text-token-text-secondary">{`${r.label}: `}</span>
                  {r.value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <CardSourceToggle raw={raw} />
    </CardShell>
  );
};

// equal `raw` ⇒ identical card, so skip re-renders while a sibling block streams
const propsAreEqual = (prev: TradeCardProps, next: TradeCardProps) =>
  prev.raw != null
  && prev.raw === next.raw
  && prev.ticker === next.ticker
  && prev.companyName === next.companyName;

export default memo(TradeCard, propsAreEqual);
